import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import adminAPI from "../apiClient";
import type { ConversationSummary, PaginationInfo, ConversationFilters } from "../types";
import {
  EMPTY_DRAFT,
  FILTER_DEBOUNCE_MS,
  mergeDraft,
  type DebouncedField,
  type FilterDraft,
} from "./filterDraft";

// hasTurns starts true so 0-turn sessions are hidden out of the box: the web app
// writes a conversation row on every page load / project rehydrate, long before the
// user types anything, so sessions nobody spoke in otherwise dominate the list.
const DEFAULT_FILTERS: ConversationFilters = {
  username: "",
  environment: "",
  channel: "",
  dateFrom: "",
  dateTo: "",
  search: "",
  hasErrors: undefined,
  hasPositiveFeedback: undefined,
  hasNegativeFeedback: undefined,
  hitLimits: undefined,
  hasTurns: true,
  minTimeMs: "",
  maxTimeMs: "",
  page: 1,
  page_size: 50,
};

export type FilterValue = string | boolean | number | undefined;

export interface ConversationList {
  conversations: ConversationSummary[];
  pagination: PaginationInfo | null;
  loading: boolean;
  error: string | null;
  filters: ConversationFilters;
  /** What is currently typed in the free-text boxes, before debouncing. */
  draft: FilterDraft;
  handleFilterChange: (field: keyof ConversationFilters, value: FilterValue) => void;
  handleDraftChange: (field: DebouncedField, value: string) => void;
  handlePageChange: (newPage: number) => void;
  clearFilters: () => void;
}

/** A `conversationId` query parameter is a deep link to that conversation. */
function useConversationDeepLink(): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const conversationIdParam = urlParams.get("conversationId");

    if (conversationIdParam) {
      // Navigate directly to the conversation detail page
      void navigate(`/admin/conversation/${conversationIdParam}`);
    }
  }, [location.search, navigate]);
}

/**
 * Hold the free-text filters back until typing pauses.
 *
 * Every keystroke used to fire its own Snowflake query. The applied filters
 * now only move once the user stops for FILTER_DEBOUNCE_MS.
 */
function useDebouncedDraft(
  draft: FilterDraft,
  setFilters: React.Dispatch<React.SetStateAction<ConversationFilters>>
): void {
  useEffect(() => {
    const timer = setTimeout(
      () => setFilters((prev) => mergeDraft(prev, draft)),
      FILTER_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [draft, setFilters]);
}

/** The filtered, paginated conversation list and the controls that drive it. */
interface FetchSetters {
  setConversations: (rows: ConversationSummary[]) => void;
  setPagination: (pagination: PaginationInfo | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

/**
 * Fetch the list whenever the applied filters change.
 *
 * A superseded response is dropped rather than rendered: filters change as
 * fast as someone types, so a slow early request could otherwise land after a
 * newer one and show rows for a filter that is no longer on screen.
 */
function useFetchConversations(filters: ConversationFilters, set: FetchSetters): void {
  const { setConversations, setPagination, setLoading, setError } = set;

  useEffect(() => {
    let cancelled = false;

    const fetchConversations = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const response = await adminAPI.getConversations(filters);
        if (cancelled) return;
        setConversations(response.conversations);
        setPagination(response.pagination);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchConversations();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]); // Refetch when filters change
}

export function useConversationList(): ConversationList {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ConversationFilters>(DEFAULT_FILTERS);
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_DRAFT);

  useConversationDeepLink();
  useDebouncedDraft(draft, setFilters);
  useFetchConversations(filters, { setConversations, setPagination, setLoading, setError });

  const handleFilterChange = (field: keyof ConversationFilters, value: FilterValue): void => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handleDraftChange = (field: DebouncedField, value: string): void => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handlePageChange = (newPage: number): void => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const clearFilters = (): void => {
    setDraft(EMPTY_DRAFT);
    // A fresh object so a clear always refetches, even when nothing was set
    setFilters({ ...DEFAULT_FILTERS });
  };

  return {
    conversations,
    pagination,
    loading,
    error,
    filters,
    draft,
    handleDraftChange,
    handleFilterChange,
    handlePageChange,
    clearFilters,
  };
}
